import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import './dopamine-theme.css'; // 引入多巴胺主题样式

// 导入组件
import Navbar from './components/Navbar';
import Login from './pages/Login';
import Register from './pages/Register';
import Home from './pages/Home';
import ActivityList from './pages/ActivityList';
import ActivityDetail from './pages/ActivityDetail';
import ActivityImportSolitaire from './pages/ActivityImportSolitaire';
import UserActivities from './pages/UserActivities';
import UserPoints from './pages/UserPoints';
import AdminDashboard from './pages/admin/Dashboard';
import AdminUsers from './pages/admin/Users';
import AdminActivities from './pages/admin/Activities';
import AdminUserActivities from './pages/admin/UserActivities';
import PointsManagement from './pages/admin/PointsManagement';
import PointsLogs from './pages/admin/PointsLogs';
import Shop from './pages/Shop';
import ShopOrders from './pages/ShopOrders';
import ShopAdmin from './pages/ShopAdmin';
import AdminShopOrders from './pages/AdminShopOrders';
// 微信端H5页面
import { WxEntry, WxHome, WxActivity, WxActivityDetail } from './wx';
import SystemConfig from './pages/SystemConfig';
import { setupWeChatShare } from './utils/wechat';

function WeChatShareManager() {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname.startsWith('/activities/') && location.pathname.length > '/activities/'.length) {
      return;
    }
    if (location.pathname.startsWith('/shop')) {
      setupWeChatShare({
        title: '积分商城',
        desc: '兑换好礼，乐享福利',
        link: window.location.href,
        imgUrl: '/images/shop_default.png',
        type: 'shop',
      });
    } else {
      setupWeChatShare({
        title: '职工之家',
        desc: '精彩活动，积分好礼',
        link: window.location.href,
        imgUrl: '/images/activity_default.png',
        type: 'activity',
      });
    }
  }, [location]);
  return null;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user')) || {});

  // 微信环境自动授权逻辑
  useEffect(() => {
    const isWeChat = /micromessenger/i.test(window.navigator.userAgent);
    const token = localStorage.getItem('token');
    const isWxEntry = window.location.pathname.startsWith('/wx/entry');
    // 只要没登录，且在微信环境，且不是授权回调页，就自动跳转到微信授权
    if (!token && isWeChat && !isWxEntry) {
      // 跳转到微信授权入口，带上原始目标页
      window.location.href = `/wx/entry?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`;
      return;
    }
  }, []);

  const login = (token, user) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    setIsAuthenticated(true);
    setUser(user);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser({});
  };

  return (
    <Router>
      <WeChatShareManager />
      <div className="App">
        <Navbar isAuthenticated={isAuthenticated} user={user} logout={logout} />
        <div className="container mt-4">
          <Routes>
            {/* 微信端H5专用路由，不做登录拦截 */}
            <Route path="/wx/entry" element={<WxEntry />} />
            <Route path="/wx/home" element={<WxHome />} />
            <Route path="/wx/activity" element={<WxActivity />} />
            <Route path="/wx/activity/:id" element={<WxActivityDetail />} />

            {/* 原有网页端路由 */}
            <Route path="/login" element={!isAuthenticated ? <Login login={login} /> : <Navigate to="/" />} />
            <Route path="/register" element={!isAuthenticated ? <Register /> : <Navigate to="/" />} />
            <Route path="/" element={isAuthenticated ? <Home user={user} /> : <Navigate to="/login" />} />
            <Route path="/activities" element={isAuthenticated ? <ActivityList /> : <Navigate to="/login" />} />
            <Route path="/activities/:id" element={isAuthenticated ? <ActivityDetail /> : <Navigate to="/login" />} />
           <Route path="/activities/:id/import-solitaire" element={isAuthenticated ? <ActivityImportSolitaire /> : <Navigate to="/login" />} />
            <Route path="/my-activities" element={isAuthenticated ? <UserActivities /> : <Navigate to="/login" />} />
            <Route path="/my-points" element={isAuthenticated ? <UserPoints /> : <Navigate to="/login" />} />
            <Route path="/user-points" element={isAuthenticated ? <UserPoints /> : <Navigate to="/login" />} />
            {/* 积分商城相关路由 */}
            <Route path="/shop" element={isAuthenticated ? <Shop /> : <Navigate to="/login" />} />
            <Route path="/shop/orders" element={isAuthenticated ? <ShopOrders /> : <Navigate to="/login" />} />
            <Route path="/admin/shop" element={isAuthenticated && (user.role === 'admin' || user.role === 'union') ? <ShopAdmin /> : <Navigate to="/" />} />
            <Route path="/admin/shop-orders" element={isAuthenticated && (user.role === 'admin' || user.role === 'union') ? <AdminShopOrders /> : <Navigate to="/" />} />
            {/* 管理路由 */}
            {/* 仅管理员可访问的路由 */}
            <Route 
              path="/admin" 
              element={isAuthenticated && user.role === 'admin' ? <AdminDashboard /> : <Navigate to="/" />} 
            />
            <Route 
              path="/admin/points" 
              element={isAuthenticated && user.role === 'admin' ? <PointsManagement /> : <Navigate to="/" />} 
            />
            <Route 
              path="/admin/points-logs" 
              element={isAuthenticated && user.role === 'admin' ? <PointsLogs /> : <Navigate to="/" />} 
            />
            {/* 系统配置路由，仅管理员可访问 */}
            <Route 
              path="/admin/system-config" 
              element={isAuthenticated && user.role === 'admin' ? <SystemConfig /> : <Navigate to="/" />} 
            />
            {/* 管理员和工会委员可访问的路由 */}
            <Route 
              path="/admin/users" 
              element={isAuthenticated && (user.role === 'admin' || user.role === 'union') ? <AdminUsers /> : <Navigate to="/" />} 
            />
            <Route 
              path="/admin/activities" 
              element={isAuthenticated && (user.role === 'admin' || user.role === 'union') ? <AdminActivities /> : <Navigate to="/" />} 
            />
            <Route 
              path="/admin/user-activities" 
              element={isAuthenticated && (user.role === 'admin' || user.role === 'union') ? <AdminUserActivities /> : <Navigate to="/" />} 
            />
          </Routes>
        </div>
      </div>
    </Router>
  );
}

export default App;
